(ns app.front.view.group.admin.bonds.groups
  "Events and subscriptions for bonds administrators view"
  (:require [app.domain.bond.model :as bond-model]
            [app.domain.group.model :as group-model]
            [app.front.entities :as entities]
            [app.front.view.bond.common :as view-common]
            [app.front.view.bond.util :as bond-util]
            [clojure.set :as set]
            [clojure.string :as str]
            [re-frame.core-instrumented :as rf]))

(rf/reg-event-fx ::fetch-group-bonds
  (fn [_ [_ group-id]]
    {:dispatch [:comm/command-monitored
                {:command    :bond/get-bonds
                 :data       {:group-id group-id}
                 :method     :get
                 :on-success [::entities/merge-entity-db]}]}))

(rf/reg-event-db ::set-filter-text
  (rf/path ::state)
  (fn [state [_ value]]
    (assoc state :filter-text value)))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::filter-text
  :<- [::state]
  :-> :filter-text)

(defn- bonds-sort
  "Sorts bonds into three ordered groups:

  1. Expiring bonds (has `:bond/expires-on`) - ascending by expiry date, soonest first
  2. Active bonds (no expiry) - descending by `:entity/created`, newest first
  3. Cancelled invitations - always at the bottom, unsorted"
  [bonds]
  (let [group     (fn [bond] (cond (bond-model/cancelled-invite? bond) 2
                                   (:bond/expires-on bond)             0
                                   :else                               1))
        sort-date (fn [bond] (if-let [expires (:bond/expires-on bond)]
                               (.getTime expires)
                               (- (:entity/created bond))))]
    (sort-by (juxt group sort-date) bonds)))

(defn- bonds-filter [bond search-term lang tr]
  (let [owner-or-bond (or (:bond/owner bond) bond)]
    (or (str/blank? search-term)
        (str/includes? (str/lower-case (str (bond-util/format-bond-owner lang owner-or-bond tr))) search-term)
        (str/includes? (str/lower-case (str (view-common/format-owner-email owner-or-bond))) search-term))))

(rf/reg-sub ::group-bonds
  (fn [[_ group-id]]
    [(rf/subscribe [::entities/entities {:entity/parent group-id
                                         :entity/type   :entity.type/bond}])
     (rf/subscribe [::filter-text])
     (rf/subscribe [:language])
     (rf/subscribe [:tr])])
  (fn [[bonds filter-text lang tr] _]
    (let [search-term (some-> filter-text str/lower-case)]
      (->> bonds
           (filter #(bonds-filter % search-term lang tr))
           bonds-sort))))

(rf/reg-sub ::authority-groups
  (fn [[_ group-id]]
    (rf/subscribe [::entities/entities {:entity/parent group-id
                                        :entity/type   :entity.type/group
                                        :group/type    :group.type/application-authority}]))
  (fn [groups _]
    (->> groups
         (filter (comp bond-model/authority-groups-with-bonds group-model/primary-permit-type))
         (sort-by :group/name))))

(rf/reg-sub ::groups-with-bonds
  (fn [[_ group-id]]
    (rf/subscribe [::authority-groups group-id]))
  (fn [groups _]
    (->> groups
         (filter #(group-model/has-feature? % :group.feature/use-bond-accounts))
         set)))

(rf/reg-sub ::edit-pending-ids :-> ::edit-pending-ids)

(rf/reg-sub ::disabled-groups
  (fn [[_ group-id]]
    [(rf/subscribe [::authority-groups group-id])
     (rf/subscribe [::edit-pending-ids])])
  (fn [[groups ids] _]
    (->> groups
         (filter #(contains? ids (:db/id %)))
         set)))

(rf/reg-event-db ::clear-pending
  (fn [db [_ group-id]]
    (update db ::edit-pending-ids disj group-id)))

(defn toggle-fn [db enabled?]
  (fn [group-id]
    [:dispatch [:comm/command-monitored
                {:command          :group/toggle-authority-group-feature-from-domain
                 :data             {:group-id       group-id
                                    :feature        :group.feature/use-bond-accounts
                                    :enabled?       enabled?
                                    :entity/version (get-in db [::entities/entity-db group-id :entity/version])}
                 :on-success       [::toggle-bonds.success]
                 :finally-dispatch [::clear-pending group-id]}]]))

(rf/reg-event-fx ::toggle-bonds.success
  (fn [_ [_ status resp]]
    {:dispatch-n [[::entities/apply-changes status resp]
                  [:notifications/add :toast :success :success :bond.config/update-success]]}))

(rf/reg-event-fx ::toggle-bonds
  (fn [{:keys [db]} [_ add-for remove-from]]
    {:db (update db ::edit-pending-ids set/union add-for remove-from)
     :fx (concat
           (map (toggle-fn db true) add-for)
           (map (toggle-fn db false) remove-from))}))
